import React, {useEffect, useState} from "react";
import {Box, Button, CircularProgress, Typography} from "@mui/material";
import {downloadLotCertificate, GenealogyEntry, readReleasedLot, ReleasedDocument,
        ReleasedLot, ReleasedLotDetail} from "../util/api";
import {amount} from "./Lots";

/**
 * One released lot, for the person it was released to.
 *
 * THREE QUESTIONS AND LEAVE: what batch is this, where is its certificate, and what went into it.
 * Everything on this page answers one of those and nothing answers a fourth.
 *
 * THE PAGE SAYS WHAT IT CANNOT TELL YOU, twice, and both sentences are load-bearing.
 *
 *   The documents are the set that was RELEASED, not the set the lot now carries. A corrected
 *   certificate attached afterwards does not appear here until somebody releases it too -- and a
 *   reader holding one certificate has no way to discover that from the page unless the page says
 *   so. That is the honest cost of naming files one by one, which is the model that stops a
 *   deviation report being published by accident.
 *
 *   An empty genealogy means EITHER that nothing went into this lot OR that the manufacturer did
 *   not disclose what did. Those are different facts and a blank panel reads as the first, so the
 *   two are drawn differently.
 */
const HEADING = "#2B2B2B";
const MUTED = "#717171";
const BORDER = "#E0E0E0";

function when(value?: string | null): string {
    if (!value) return "not recorded";
    const at = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 10);
    return at.toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
}

function size(bytes?: number | null): string {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function Field({label, value}: {label: string; value: React.ReactNode}) {
    return (
        <Box sx={{minWidth: 150}}>
            <Typography sx={{fontSize: 12, color: MUTED}}>{label}</Typography>
            <Typography sx={{fontSize: 15, color: HEADING, mt: 0.25}}>{value}</Typography>
        </Box>
    );
}

export default function Lot({summary, onBack}: {summary: ReleasedLot; onBack: () => void}) {
    const [lot, setLot] = useState<ReleasedLotDetail | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [taking, setTaking] = useState<string | null>(null);
    const [downloadError, setDownloadError] = useState<string | null>(null);

    useEffect(() => {
        readReleasedLot(summary.USID).then(result => {
            if (result.status === 200) setLot(result.data);
            // 404 IS THE ANSWER FOR REVOKED, EXPIRED AND NEVER-EXISTED ALIKE, and the page repeats
            // it rather than improving on it: saying "your access was withdrawn" would confirm the
            // lot is real, which is the fact a withdrawal removes.
            else setError(result.data?.Cause || "That lot was not found.");
            setLoading(false);
        });
    }, [summary.USID]);

    // THE BROWSER FOLLOWS THE URL, we do not fetch it. A presigned GET answered into JavaScript
    // would have to be turned back into a file by hand, and the Content-Disposition the service
    // asked S3 for -- which is what names the saved file -- would be thrown away in the process.
    const take = async (document: ReleasedDocument) => {
        setTaking(document.AttachmentUSID);
        setDownloadError(null);
        const result = await downloadLotCertificate(summary.USID, document.AttachmentUSID);
        setTaking(null);
        if (result.status === 200 && result.data?.Url) {
            window.location.href = result.data.Url;
            return;
        }
        setDownloadError(result.data?.Cause || "That download could not be prepared.");
    };

    const documents: ReleasedDocument[] = lot?.Documents ?? [];
    const genealogy: GenealogyEntry[] = lot?.Genealogy ?? [];
    const generations = Array.from(new Set(genealogy.map(one => one.Generation))).sort();

    return (
        <Box sx={{maxWidth: 900, mx: "auto", px: 3, py: 6, boxSizing: "border-box"}}>
            <Button onClick={onBack} sx={{fontSize: 13, textTransform: "none", ml: -1}}>
                &larr; Your lots
            </Button>

            <Typography component="h1" sx={{fontSize: 24, fontWeight: 500, color: HEADING, mt: 1}}>
                {summary.LotNumber || summary.Name || "Untitled lot"}
            </Typography>
            <Typography sx={{fontSize: 13, color: MUTED, mt: 0.5}}>
                {summary.From ? `Released to you by ${summary.From}` : "Released to you"}
            </Typography>

            {error && <Typography sx={{fontSize: 14, color: "#B3261E", mt: 4}}>{error}</Typography>}
            {loading && !error && (
                <Box sx={{display: "flex", py: 6}}><CircularProgress size={22}/></Box>
            )}

            {lot && !error && (
                <>
                    <Box sx={{display: "flex", gap: 4, flexWrap: "wrap", mt: 4, p: 3,
                              border: `1px solid ${BORDER}`, borderRadius: 2}}>
                        <Field label="Lot number" value={lot.LotNumber || "—"}/>
                        <Field label="Material"
                               value={lot.Item?.Name || lot.Name || "—"}/>
                        {lot.Item?.ObjectID && (
                            <Field label="Part number" value={lot.Item.ObjectID}/>
                        )}
                        <Field label="Quantity" value={amount(lot) || "—"}/>
                        <Field label="Shipped" value={when(lot.ShippedDatetime)}/>
                        {lot.ShipmentReference && (
                            <Field label="Shipment" value={lot.ShipmentReference}/>
                        )}
                    </Box>

                    {lot.ExpiresDatetime && (
                        <Typography sx={{fontSize: 12, color: MUTED, mt: 2}}>
                            Your access to this lot runs to {when(lot.ExpiresDatetime)}.
                        </Typography>
                    )}

                    <Typography component="h2"
                                sx={{fontSize: 16, fontWeight: 600, color: HEADING, mt: 5}}>
                        Documents{documents.length ? ` (${documents.length})` : ""}
                    </Typography>

                    <Box sx={{mt: 2, border: `1px solid ${BORDER}`, borderRadius: 2,
                              overflow: "hidden"}}>
                        {documents.map((one, index) => (
                            <Box key={one.AttachmentUSID}
                                 sx={{display: "flex", gap: 2, px: 2.5, py: 1.75,
                                      alignItems: "center", flexWrap: "wrap",
                                      borderTop: index ? `1px solid ${BORDER}` : "none"}}>
                                <Box sx={{flex: 1, minWidth: 220}}>
                                    <Typography sx={{fontSize: 14, color: HEADING, fontWeight: 500}}>
                                        {one.Kind || one.Name || "Document"}
                                    </Typography>
                                    <Typography sx={{fontSize: 12, color: MUTED, mt: 0.25}}>
                                        {one.Kind && one.Name ? `${one.Name} · ` : ""}
                                        {size(one.Bytes)}
                                    </Typography>
                                </Box>
                                {/* A FILE WHOSE BYTES ARE GONE IS STILL LISTED. The record says it
                                    shipped with this lot, and removing the row would lose that
                                    fact as well as the file -- so the row stays and says what
                                    happened instead of offering a download that cannot work. */}
                                {one.Missing ? (
                                    <Typography sx={{fontSize: 13, color: "#B54708"}}>
                                        No longer in storage &mdash; ask your contact to attach it again
                                    </Typography>
                                ) : (
                                    <Button size="small" variant="outlined"
                                            disabled={taking !== null}
                                            onClick={() => take(one)}
                                            sx={{textTransform: "none", fontSize: 13}}>
                                        {taking === one.AttachmentUSID ? "Preparing…" : "Download"}
                                    </Button>
                                )}
                            </Box>
                        ))}
                        {documents.length === 0 && (
                            <Typography sx={{fontSize: 13, color: MUTED, p: 2.5, lineHeight: 1.6}}>
                                No documents were released with this lot. Which papers go out with a
                                batch is the manufacturer's decision &mdash; ask your contact there
                                if you were expecting a certificate.
                            </Typography>
                        )}
                    </Box>

                    {downloadError && (
                        <Typography sx={{fontSize: 13, color: "#B3261E", mt: 1}}>
                            {downloadError}
                        </Typography>
                    )}

                    {/* THE SENTENCE THAT KEEPS THIS PAGE HONEST, and the reason it is here rather
                        than in a tooltip: a reader holding one certificate cannot otherwise find
                        out that a corrected one might exist. */}
                    {documents.length > 0 && lot.DocumentsAreAsReleased && (
                        <Typography sx={{fontSize: 12, color: MUTED, mt: 2, lineHeight: 1.6}}>
                            These are the documents released with this lot. If a certificate has
                            been corrected or reissued since, it appears here only once the
                            manufacturer releases it too.
                        </Typography>
                    )}

                    <Typography component="h2"
                                sx={{fontSize: 16, fontWeight: 600, color: HEADING, mt: 5}}>
                        What went into it
                    </Typography>

                    {/* TWO DIFFERENT EMPTY STATES, because they are two different facts and a blank
                        panel would be read as the first one. */}
                    {!lot.GenealogyDisclosed ? (
                        <Typography sx={{fontSize: 13, color: MUTED, mt: 2, lineHeight: 1.6}}>
                            The manufacturer has not released the genealogy of this lot. That is a
                            decision they make per shipment, not a gap in the record &mdash; ask
                            your contact there if you need it.
                        </Typography>
                    ) : genealogy.length === 0 ? (
                        <Typography sx={{fontSize: 13, color: MUTED, mt: 2, lineHeight: 1.6}}>
                            Nothing is recorded as having been consumed into this lot.
                        </Typography>
                    ) : (
                        <Box sx={{mt: 2, border: `1px solid ${BORDER}`, borderRadius: 2,
                                  overflow: "hidden"}}>
                            {generations.map(generation => (
                                <Box key={generation}>
                                    <Typography sx={{fontSize: 12, color: MUTED, px: 2.5, pt: 2,
                                                     pb: 1, backgroundColor: "#FAFAFA"}}>
                                        {generation === 1
                                            ? "Consumed into this lot"
                                            : `Consumed into those, ${generation} steps back`}
                                    </Typography>
                                    {genealogy.filter(one => one.Generation === generation)
                                              .map(one => (
                                        <Box key={one.ObjectUSID}
                                             sx={{display: "flex", gap: 2, px: 2.5, py: 1.5,
                                                  flexWrap: "wrap",
                                                  borderTop: `1px solid ${BORDER}`}}>
                                            <Typography sx={{fontSize: 14, color: HEADING,
                                                             fontWeight: 500, minWidth: 140}}>
                                                {one.ObjectID || "—"}
                                            </Typography>
                                            <Typography sx={{fontSize: 14, color: MUTED,
                                                             minWidth: 200}}>
                                                {one.Name}
                                            </Typography>
                                            <Typography sx={{fontSize: 13, color: MUTED}}>
                                                {one.Type || ""}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}
